import { Group, MeshLambertMaterial } from "three"
import type { NeighborElevationCollection } from "../../../../../rx/memo/useNodeNeighborBaseFloorElevationReactiveMap"
import type { EntityGraphNode } from "../../../../core/engine/EntityGraphNode/EntityGraphNode.type"
import type { JungleEntity } from "../../../../core/entity/EntityType/Jungle"
import createBatchedMeshWithGeometryList from "../../../fn/createBatchedMeshWithGeometryList"
import enableShadows from "../../../fn/enableShadows"
import { dummyMatrix, dummyObject3D, dummyPosition, dummyRotation, dummyScale, resetDummyObject3D } from "../../DummyObject3D/dummyObject3D"
import generateTreePositionsV3 from "../Forest/generateTreePositionsV3"
import loadAndMergeJungleTreeGeometry from "./loadAndMergeJungleTreeGeometry"

// const lowResJungleNoise = createCamoMapTexture(tmpRngWith(2), 16, 8)

export default async function createJungleTerrainClusterGroup(
  list: Array<EntityGraphNode<JungleEntity>>,
  neighborBaseElevationReactiveMap: Map<number, NeighborElevationCollection>,
): Promise<Group> {
  const group = new Group()
  group.name = 'JungleTerrainCluster'
  
  try {
    const treeVariants = await loadAndMergeJungleTreeGeometry()
    if (treeVariants.length === 0) {
      console.error('Failed to load palm tree geometries')
      return group
    }

    const material = new MeshLambertMaterial({
      vertexColors: true,
    })

    const positions = generateTreePositionsV3(list, treeVariants.length, neighborBaseElevationReactiveMap)

    // Create BatchedMesh with initial capacity
    const batchSize = positions.length
    const batchedMesh = createBatchedMeshWithGeometryList(batchSize, treeVariants, material)
    
    // Add all tree variant geometries to the batched mesh
    const geometryIds = treeVariants.map(geometry => batchedMesh.addGeometry(geometry))
    
    resetDummyObject3D()
    dummyScale.multiplyScalar(0.5)
    
    const instanceIds: number[] = []
    
    // Add instances with their respective variant geometries
    for (let i = 0; i < positions.length; i++) {
      const { v: variantIndex } = positions[i]
      const geometryId = geometryIds[variantIndex]
      const instanceId = batchedMesh.addInstance(geometryId)
      instanceIds.push(instanceId)
    }
    
    // Set matrices for all instances
    for (let i = 0; i < positions.length; i++) {
      const { x, y, r, h } = positions[i]
      dummyPosition.x = x
      dummyPosition.z = y
      dummyPosition.y = h
      dummyRotation.y = r
      dummyObject3D.updateMatrix()
      batchedMesh.setMatrixAt(instanceIds[i], dummyMatrix)
    }
    
    enableShadows(batchedMesh)
    group.add(batchedMesh)
    
    return group
  } catch (error: unknown) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred'
    console.error('Error creating Jungle terrain cluster:', errorMessage)
    // Return the group even if there was an error to avoid breaking the app
    return group
  }
}
